import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { getTermsAndConditionsActionInitiate, getPrivacyPolicyActionInitiate, getCancellationRefundPolicyActionInitiate } from "../../redux/actions/common/getLegalContentAction";
import { termsFallback, privacyFallback, cancellationFallback } from "../../utils/legalFallbacks";
import DOMPurify from 'dompurify';
import CommonModel from "./CommonModel";
import colors from "../../theme/colors";
import "./LegalContent.css";

export default function LegalContent({ type = "terms", isModal = false, isOpen = false, onClose = null }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const hasCalledRef = React.useRef(false);

  const termsState = useSelector((state) => state.getTermsAndConditions);
  const privacyState = useSelector((state) => state.getPrivacyPolicy);
  const cancellationState = useSelector((state) => state.getCancellationRefundPolicy);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Only fetch once per component lifecycle to prevent infinite loops
    // Ref-based gate (hasCalledRef) prevents re-runs on dispatch/state changes
    // Including dependencies would cause infinite API calls on every state update
    if (!hasCalledRef.current && !termsState?.data && !privacyState?.data && !cancellationState?.data) {
      hasCalledRef.current = true;
      dispatch(getTermsAndConditionsActionInitiate());
      dispatch(getPrivacyPolicyActionInitiate());
      dispatch(getCancellationRefundPolicyActionInitiate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isTerms = type === "terms";
  const isPrivacy = type === "privacy";
  const isCancellation = type === "cancellation";

  let content, title, navItems;

  if (isTerms) {
    content = termsState?.data || termsFallback;
    title = "Terms & Conditions";
  } else if (isPrivacy) {
    content = privacyState?.data || privacyFallback;
    title = "Privacy Policy";
  } else if (isCancellation) {
    content = cancellationState?.data || cancellationFallback;
    title = "Cancellation & Refund Policy";
  }

  const contentDiv = (
    <div style={{ color: "var(--text-primary)" }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />
  );

  if (isModal) {
    return (
      <CommonModel
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        width={800}
      >
        {contentDiv}
      </CommonModel>
    );
  }

  navItems = [
    {
      title: "Terms & Conditions",
      path: "/terms_conditions",
      icon: "ph:file-text-bold",
      isActive: isTerms
    },
    {
      title: "Privacy Policy",
      path: "/privacy",
      icon: "ph:shield-check-bold",
      isActive: isPrivacy
    },
    {
      title: "Cancellation & Refund",
      path: "/refund_policy",
      icon: "ph:receipt-bold",
      isActive: isCancellation
    }
  ];

  const getHeroContent = () => {
    if (isTerms) {
      return {
        icon: "ph:scroll-bold",
        color: colors.mainColor,
        bgGradient: "linear-gradient(135deg, rgba(79, 172, 254, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)",
        description: "Please read our terms carefully to understand your rights and responsibilities"
      };
    }
    if (isPrivacy) {
      return {
        icon: "ph:shield-check-bold",
        color: "#10b981",
        bgGradient: "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(6, 78, 59, 0.05) 100%)",
        description: "We are committed to protecting your privacy and ensuring you have a positive experience"
      };
    }
    if (isCancellation) {
      return {
        icon: "ph:receipt-bold",
        color: "#f59e0b",
        bgGradient: "linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)",
        description: "Easy cancellation and refund process with transparent policies"
      };
    }
  };

  const hero = getHeroContent();

  return (
    <div className="legal-content-wrapper" style={{ minHeight: "100vh", background: "var(--bg-primary)" }}>
      {/* Header with Navigation */}
      <div className="legal-header" style={{
        background: "var(--bg-surface-1)",
        borderBottom: "1px solid var(--border-subtle)",
        padding: isMobile ? "10px 8px" : "16px 24px",
        position: "sticky",
        top: 0,
        zIndex: 100,
        backdropFilter: "blur(10px)",
        backgroundColor: "rgba(var(--bg-surface-1-rgb), 0.95)"
      }}>
        <div style={{
          maxWidth: isMobile ? "100%" : 1200,
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
          overflowX: isMobile ? "auto" : "visible",
          overflowY: "hidden",
          scrollBehavior: "smooth"
        }}>
          <div className="legal-nav-buttons" style={{
            display: "flex",
            gap: isMobile ? 6 : 16,
            flexWrap: "nowrap",
            justifyContent: "flex-start",
            alignItems: "center",
            minWidth: isMobile ? "max-content" : "auto"
          }}>
            {navItems.map((item, idx) => (
              <button
                key={idx}
                onClick={() => !item.isActive && navigate(item.path)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: isMobile ? 4 : 6,
                  padding: isMobile ? "8px 12px" : "12px 18px",
                  background: item.isActive
                    ? isMobile ? colors.mainColor : `${colors.mainColor}15`
                    : isMobile ? "var(--bg-surface-2)" : "transparent",
                  border: `2px solid ${item.isActive ? colors.mainColor : isMobile ? "var(--border-subtle)" : "transparent"}`,
                  borderRadius: isMobile ? 10 : 24,
                  cursor: item.isActive ? "default" : "pointer",
                  transition: "all 0.3s ease",
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: item.isActive ? 600 : 500,
                  color: item.isActive
                    ? isMobile ? "white" : colors.mainColor
                    : "var(--text-secondary)",
                  flex: isMobile ? "0 0 auto" : "auto",
                  minHeight: isMobile ? "36px" : "auto",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                  overflow: "visible",
                  display: "inline-flex"
                }}
                onMouseEnter={(e) => {
                  if (!item.isActive) {
                    e.currentTarget.style.background = "var(--bg-surface-3)";
                    e.currentTarget.style.borderColor = colors.mainColor;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!item.isActive) {
                    e.currentTarget.style.background = "var(--bg-surface-2)";
                    e.currentTarget.style.borderColor = "var(--border-subtle)";
                  }
                }}
              >
                <Icon
                  icon={item.icon}
                  width={18}
                  height={18}
                  color={item.isActive ? (isMobile ? "white" : colors.mainColor) : "var(--text-secondary)"}
                />
                <span>{item.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div style={{
        background: hero.bgGradient,
        padding: isMobile ? "40px 16px" : "60px 24px",
        textAlign: "center",
        borderBottom: "1px solid var(--border-subtle)"
      }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div style={{
            width: isMobile ? 60 : 80,
            height: isMobile ? 60 : 80,
            borderRadius: "50%",
            background: `${hero.color}20`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
            border: `2px solid ${hero.color}40`
          }}>
            <Icon icon={hero.icon} width={isMobile ? 28 : 40} height={isMobile ? 28 : 40} color={hero.color} />
          </div>
          <h1 style={{
            fontSize: isMobile ? "24px" : "40px",
            fontWeight: 600,
            marginBottom: 12,
            background: `linear-gradient(135deg, var(--text-primary), var(--text-secondary))`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            lineHeight: 1.3
          }}>
            {title}
          </h1>
          <p style={{
            fontSize: isMobile ? "14px" : "16px",
            color: "var(--text-secondary)",
            lineHeight: 1.6,
            marginBottom: 0,
            paddingLeft: isMobile ? "8px" : "0",
            paddingRight: isMobile ? "8px" : "0"
          }}>
            {hero.description}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        padding: isMobile ? "24px 16px" : "48px 24px",
        maxWidth: 1000,
        margin: "0 auto",
        width: "100%",
        boxSizing: "border-box"
      }}>
        <div style={{
          background: "var(--bg-surface-1)",
          borderRadius: isMobile ? 12 : 16,
          border: "1px solid var(--border-subtle)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          overflow: "hidden"
        }}>
          {/* Last Updated Badge */}
          <div style={{
            padding: isMobile ? "16px" : "20px 32px",
            borderBottom: "1px solid var(--border-subtle)",
            background: "linear-gradient(135deg, var(--bg-surface-2) 0%, var(--bg-surface-1) 100%)"
          }}>
            {/* <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Icon icon="ph:clock-bold" width={16} color="var(--text-secondary)" />
              <span style={{
                fontSize: "12px",
                color: "var(--text-secondary)",
                lineHeight: 1.4
              }}>
                Last updated: January 2024 • ~5 min read
              </span>
            </div> */}
          </div>

          {/* Content */}
          <div style={{
            padding: isMobile ? "24px 16px" : "40px 32px",
            lineHeight: 1.8,
            fontSize: isMobile ? "14px" : "15px"
          }}>
            <div style={{
              color: "var(--text-primary)"
            }}>
              {contentDiv}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
